import type { DocumentItem } from './model.js';
import { getValueAtPath } from './path.js';

export interface SearchOptions {
  /** Extra dot paths to match against, e.g. `['metadata.owner']`. */
  searchableFields?: string[];
  /** Match on substring anywhere (default) or only at word starts. */
  matchMode?: 'contains' | 'startsWith';
}

/**
 * Lower-case and strip diacritics so "resume" finds "résumé".
 * Cached because it runs once per item per keystroke.
 */
const foldCache = new Map<string, string>();
export function fold(value: string): string {
  const hit = foldCache.get(value);
  if (hit !== undefined) return hit;
  const folded = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  // Bounded so a long session cannot grow this without limit.
  if (foldCache.size > 5000) foldCache.clear();
  foldCache.set(value, folded);
  return folded;
}

function fieldMatches(haystack: unknown, needle: string, mode: 'contains' | 'startsWith'): boolean {
  if (haystack == null) return false;
  const text = typeof haystack === 'string' ? haystack : String(haystack);
  const folded = fold(text);
  return mode === 'startsWith' ? folded.startsWith(needle) : folded.includes(needle);
}

/** Does one item match the query? Exposed so renderers can highlight matches. */
export function matchesQuery(item: DocumentItem, query: string, options: SearchOptions = {}): boolean {
  const needle = fold(query.trim());
  if (needle === '') return true;
  const mode = options.matchMode ?? 'contains';

  if (fieldMatches(item.name, needle, mode)) return true;
  for (const path of options.searchableFields ?? []) {
    if (fieldMatches(getValueAtPath(item, path), needle, mode)) return true;
  }
  return false;
}

/** Filter a list of items by query. An empty query returns the input as-is. */
export function searchDocuments(
  items: readonly DocumentItem[],
  query: string,
  options: SearchOptions = {},
): DocumentItem[] {
  if (query.trim() === '') return items as DocumentItem[];
  return items.filter((item) => matchesQuery(item, query, options));
}
